import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PasskeyDto } from '@fyr/shared';
import { api, ApiError } from '../api';
import { stamp } from '../format';
import { addPasskey, isCancelled, passkeysSupported } from '../passkeys';

/**
 * #147 M20.1 my passkeys: sign in to Fyr with a fingerprint, face or device PIN instead of an emailed code. Add one per
 * device (or one that syncs across them), rename, remove.
 */
export function Passkeys({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['passkeys'], queryFn: () => api.get<PasskeyDto[]>('/auth/passkeys') });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (p: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await p();
      await qc.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (e) {
      if (!isCancelled(e)) setError(e instanceof ApiError ? e.message : t('passkeys.failed'));
    } finally {
      setBusy(false);
    }
  };
  if (q.isError) return null; // e.g. signed in from an LMS: passkeys are managed when signed in to Fyr directly
  const keys = q.data ?? [];
  return (
    <section className="passkeys" id="passkeys">
      <h2>{t('passkeys.title')}</h2>
      <p className="muted small">{t(compact ? 'passkeys.introShort' : 'passkeys.intro')}</p>
      {keys.length > 0 && (
        <ul className="passkey-list">
          {keys.map((k) => (
            <li key={k.id}>
              <span className="passkey-main">
                <strong>{k.name}</strong>
                <span className="muted small">
                  {k.synced ? t('passkeys.synced') : t('passkeys.thisDevice')} ·{' '}
                  {k.lastUsedAt
                    ? t('passkeys.lastUsed', { when: stamp(k.lastUsedAt) })
                    : t('passkeys.added', { when: stamp(k.createdAt) })}
                </span>
              </span>
              <button
                className="btn btn-small"
                disabled={busy}
                onClick={() => {
                  const name = window.prompt(t('passkeys.renamePrompt'), k.name);
                  if (name !== null) void run(() => api.patch(`/auth/passkeys/${k.id}`, { name }));
                }}
              >
                {t('passkeys.rename')}
              </button>
              <button
                className="btn btn-small"
                disabled={busy}
                onClick={() =>
                  window.confirm(t('passkeys.removeConfirm', { name: k.name })) &&
                  void run(() => api.del(`/auth/passkeys/${k.id}`))
                }
              >
                {t('passkeys.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {passkeysSupported() ? (
        <button className="btn" disabled={busy} onClick={() => void run(() => addPasskey())}>
          + {t('passkeys.add')}
        </button>
      ) : (
        <p className="muted small">{t('passkeys.unsupported')}</p>
      )}
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
